import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Tag,
  Select,
  Button,
  Typography,
  Descriptions,
  Space,
  Alert,
  Tooltip,
  Badge,
  InputNumber,
  Divider,
  Row,
  Col,
  Switch,
} from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { patchPoItem } from "../../api/poIngestApi";

const { Text, Title } = Typography;
const { Option } = Select;

// Sentinel dropdown value for "this is a genuinely new item, not in the catalogue" — distinct
// from any real RunningSize id (Mongo ObjectIds never look like this).
const NEW_ITEM_VALUE = "__new_item__";

// ─── Helpers ──────────────────────────────────────────────────────────────

function confidenceBadge(score) {
  if (score == null) return null;
  const pct = Math.round(score * 100);
  const color = pct >= 85 ? "green" : pct >= 60 ? "orange" : "red";
  return <Tag color={color}>{pct}%</Tag>;
}

function matchIcon(status) {
  if (status === "matched")
    return <CheckCircleOutlined style={{ color: "#16a34a" }} />;
  if (status === "ambiguous")
    return <ExclamationCircleOutlined style={{ color: "#d97706" }} />;
  return <CloseCircleOutlined style={{ color: "#dc2626" }} />;
}

// Plain-text summary of a running-size candidate, used both for the searchable filter text
// and the secondary detail line — this is what actually lets an operator tell apart several
// candidates that share a near-identical name prefix (e.g. several "Goodcook | MIXING BOWL..."
// SKUs at different sizes/plies).
function candidateDetailText(c) {
  const dims = [c.length, c.width, c.height].filter((v) => v != null && v !== 0);
  const parts = [
    dims.length ? `${dims.join(" × ")} ${c.unit_type || "mm"}` : null,
    c.ply ? `${c.ply}ply` : null,
    c.casting_type || null,
    c.is_printing ? "printed" : null,
    c.is_coating ? "coated" : null,
    c.last_sale_price != null ? `₹${c.last_sale_price}/unit` : null,
  ].filter(Boolean);
  return parts.join(" · ") || "no spec on file";
}

// Returns an array of <Option> elements — called inline as `{renderRunningSizeOptions(x)}`
// inside a <Select>, NEVER as a JSX component (`<RunningSizeOptions .../>`). Antd v4's Select
// inspects `props.children` as a raw element tree to build its option list; wrapped in a custom
// component, it sees one opaque element instead of the <Option>s inside it and renders no
// options at all, even though they show up fine once the component actually mounts.
function renderRunningSizeOptions(candidates) {
  return (candidates || []).map((c) => (
    <Option
      key={c.id}
      value={c.id}
      label={c.name}
      searchText={`${c.name} ${candidateDetailText(c)} ${c.is_group_parent ? "group" : ""}`}
    >
      <div style={{ lineHeight: 1.35, padding: "2px 0" }}>
        <div>
          <Text strong style={{ fontSize: 12 }}>
            {c.name}
          </Text>{" "}
          <Tag style={{ marginLeft: 4 }}>{Math.round((c.score || 0) * 100)}%</Tag>
          {c.is_group_parent && <Tag color="purple">🔗 Group</Tag>}
        </div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {candidateDetailText(c)}
        </Text>
      </div>
    </Option>
  ));
}

function rateTag(rateCheck) {
  if (!rateCheck || rateCheck.severity === "none") return null;
  const { severity, delta_percent } = rateCheck;
  const sign = delta_percent > 0 ? "+" : "";
  const color =
    severity === "ok" ? "green" : severity === "warn" ? "orange" : "red";
  return (
    <Tag color={color}>
      {sign}
      {delta_percent?.toFixed(1)}%
    </Tag>
  );
}

// Builds the sibling member rows for a group-parent match, mirroring OrderItemModal.js's manual
// "add as group" expansion exactly: each non-parent member's quantity scales proportionally off
// the parent's order quantity, and its rate always comes from its own last_sale_price (never the
// parent's rate) since each member is priced independently in the catalogue.
function buildGroupMemberItems(itemGroup, parentQuantity) {
  const parentMember = (itemGroup.members || []).find((m) => m.is_parent);
  const parentQtyPerGroup = parentMember?.qty_per_group || 1;

  return (itemGroup.members || [])
    .filter((m) => !m.is_parent)
    .map((m) => {
      const rs = m.running_size;
      const rate = rs?.last_sale_price || 0;
      const memberQty = Math.round(parentQuantity * (m.qty_per_group / parentQtyPerGroup));
      return {
        item_name: m.name,
        item_description: `Part of group: ${itemGroup.name}`,
        running_size_id: rs?.id || null,
        subcategory: rs?.product_sub_category?.name || null,
        number_of_pieces: 1,
        length: rs?.length || 0,
        width: rs?.width || 0,
        height: rs?.height || 0,
        unit_type: rs?.unit_type || "mm",
        casting_type: rs?.casting_type || "DIE",
        ply: rs?.ply || 3,
        layers: rs?.layers || "[]",
        is_printing: rs?.is_printing || false,
        printing_config: rs?.printing || null,
        is_coating: rs?.is_coating || false,
        coating_config: rs?.coating || null,
        joining_method: rs?.joining_method || null,
        top_conversion_rate: rs?.top_conversion_rate || 0,
        box_conversion_rate: rs?.box_conversion_rate || 0,
        rs: rs?.rs || null,
        cs: rs?.cs || null,
        roll_size: rs?.rs || 0,
        cutting_size: rs?.cs || 0,
        boxes_per_sheet: rs?.boxes_per_sheet || null,
        sheets_per_box: rs?.sheets_per_box || null,
        box_type: rs?.box_type || null,
        rate_type: rs?.rate_type || null,
        packing_quantity_rs: null,
        item_remarks: null,
        quantity: memberQty,
        rate_per_unit: rate,
        total_amount: rate * memberQty,
        calculated_rate: 0,
        markup_percentage: 0,
        discount_percentage: 0,
        net_gsm: 0,
        box_weight: 0,
        material_cost_top: 0,
        material_cost_rest: 0,
        print_cost: 0,
        coating_cost_total: 0,
        coating_cost_lamination: 0,
        coating_cost_leafing: 0,
        coating_cost_spot_uv: 0,
        fixed_cost_total: 0,
        fixed_cost_per_box: 0,
        selected_machines: "{}",
        packing_role: null,
        qty_per_pack: null,
        item_group_id: itemGroup.id,
        qty_per_group: m.qty_per_group,
        is_group_parent: false,
      };
    });
}

// Convert a resolved ingestion item into the item shape the forms expect.
function resolvedItemToFormItem(resolvedItem, addAsGroup) {
  const rs = resolvedItem.match?.matched_running_size;
  const ex = resolvedItem.extracted || {};
  const itemGroup = resolvedItem.match?.item_group;
  const groupFields = addAsGroup && itemGroup
    ? {
        item_group_id: itemGroup.id,
        qty_per_group: (itemGroup.members || []).find((m) => m.is_parent)?.qty_per_group || 1,
        is_group_parent: true,
      }
    : { item_group_id: null, qty_per_group: null, is_group_parent: false };

  // Prefer the original printed dimensions + unit (e.g. inches) over the mm-converted values.
  // The order form stores whatever unit the customer's SKU uses, so re-normalising to mm here
  // just shows the operator the wrong number when they open the row.
  const hasOriginalDims = ex.length != null || ex.width != null || ex.height != null;
  const dimUnit = ex.unit_type || (hasOriginalDims ? "inches" : "mm");
  const length = hasOriginalDims ? (ex.length ?? 0) : (ex.length_mm ?? 0);
  const width = hasOriginalDims ? (ex.width ?? 0) : (ex.width_mm ?? 0);
  const height = hasOriginalDims ? (ex.height ?? 0) : (ex.height_mm ?? 0);

  if (rs) {
    return {
      item_name: rs.name,
      item_description: ex.description || null,
      running_size_id: rs.id,
      // No hardcoded fallback — the resolver already resolved this to either a real, existing
      // ProductSubCategory name (the matched RunningSize's own, or a confident fuzzy match) or
      // null. A null here means the operator needs to pick one via "Edit Item" before saving,
      // same as any manually-added item would require.
      subcategory: ex.subcategory || null,
      number_of_pieces: ex.number_of_pieces || "single piece",
      length: rs.length || 0,
      width: rs.width || 0,
      height: rs.height || 0,
      unit_type: rs.unit_type || "mm",
      casting_type: rs.casting_type || "DIE",
      ply: rs.ply || 3,
      layers: rs.layers || "[]",
      is_printing: rs.is_printing || false,
      printing_config: rs.printing || null,
      is_coating: rs.is_coating || false,
      coating_config: rs.coating || null,
      joining_method: ex.joining_method || rs.joining_method || null,
      rs: rs.rs || null,
      cs: rs.cs || null,
      roll_size: rs.rs || 0,
      cutting_size: rs.cs || 0,
      boxes_per_sheet: rs.boxes_per_sheet || null,
      sheets_per_box: rs.sheets_per_box || null,
      box_type: ex.box_type || rs.box_type || null,
      packing_quantity_rs: ex.packing_quantity_rs ?? null,
      top_conversion_rate: rs.top_conversion_rate || 0,
      box_conversion_rate: rs.box_conversion_rate || 0,
      net_gsm: 0,
      box_weight: 0,
      calculated_rate: rs.last_sale_price || ex.rate || 0,
      markup_percentage: 0,
      discount_percentage: 0,
      material_cost_top: 0,
      material_cost_rest: 0,
      print_cost: 0,
      coating_cost_total: 0,
      coating_cost_lamination: 0,
      coating_cost_leafing: 0,
      coating_cost_spot_uv: 0,
      fixed_cost_total: 0,
      fixed_cost_per_box: 0,
      selected_machines: "{}",
      quantity: ex.quantity || 0,
      rate_per_unit: ex.rate || 0,
      total_amount: ex.amount ?? (ex.quantity || 0) * (ex.rate || 0),
      item_remarks: ex.item_remarks || null,
      ...groupFields,
    };
  }

  // No match — minimal item from extraction, honouring any extra fields the template
  // extractor / LLM populated for us (subcategory, box_type, unit_type, casting, etc.).
  return {
    item_name: resolvedItem.raw_description || ex.description || "Unknown Item",
    item_description: ex.description || null,
    running_size_id: null,
    subcategory: ex.subcategory || null,
    number_of_pieces: ex.number_of_pieces || "single piece",
    length,
    width,
    height,
    unit_type: dimUnit,
    casting_type: ex.casting_type || "DIE",
    ply: ex.ply || 3,
    layers: "[]",
    is_printing: ex.is_printing === true,
    printing_config: null,
    is_coating: ex.is_coating === true,
    coating_config: null,
    joining_method: ex.joining_method || null,
    rs: ex.rs ?? null,
    cs: ex.cs ?? null,
    roll_size: ex.rs ?? 0,
    cutting_size: ex.cs ?? 0,
    boxes_per_sheet: ex.boxes_per_sheet ?? null,
    sheets_per_box: ex.sheets_per_box ?? null,
    box_type: ex.box_type || null,
    packing_quantity_rs: ex.packing_quantity_rs ?? null,
    top_conversion_rate: ex.top_conversion_rate ?? 0,
    box_conversion_rate: ex.box_conversion_rate ?? 0,
    net_gsm: 0,
    box_weight: 0,
    calculated_rate: 0,
    markup_percentage: 0,
    discount_percentage: 0,
    material_cost_top: 0,
    material_cost_rest: 0,
    print_cost: 0,
    coating_cost_total: 0,
    coating_cost_lamination: 0,
    coating_cost_leafing: 0,
    coating_cost_spot_uv: 0,
    fixed_cost_total: 0,
    fixed_cost_per_box: 0,
    selected_machines: "{}",
    quantity: ex.quantity || 0,
    rate_per_unit: ex.rate || 0,
    total_amount: ex.amount ?? (ex.quantity || 0) * (ex.rate || 0),
    item_remarks: ex.item_remarks || null,
    ...groupFields,
  };
}

// ─── Main component ───────────────────────────────────────────────────────

const PoIngestReviewModal = ({
  open,
  ingestion,
  onConfirm,
  onDiscard,
  onClose,
  afterClose,
}) => {
  const [localIngestion, setLocalIngestion] = useState(null);
  const [patchingLine, setPatchingLine] = useState(null);
  const [rateAcknowledged, setRateAcknowledged] = useState({});
  // Per-line "add as group" toggle, keyed by line_index. Undefined means "not yet touched by
  // the operator" — treated as ON by default whenever a group was detected, mirroring the manual
  // Add Item flow's auto-enable-on-detect behaviour (see groupToggleFor below).
  const [groupToggles, setGroupToggles] = useState({});

  useEffect(() => {
    if (open) {
      setLocalIngestion(null);
      setRateAcknowledged({});
      setGroupToggles({});
    }
  }, [open, ingestion?.id]);

  // Use local if we have patches, otherwise use prop
  const data = localIngestion || ingestion;
  const resolved = data?.resolved || {};
  const header = resolved.header || {};
  const customerResult = resolved.customer || {};
  const items = resolved.items || [];

  // Check if any line has a "block" rate deviation and not acknowledged
  const hasBlockedRate = items.some(
    (item) =>
      item.rate_check?.severity === "block" &&
      !rateAcknowledged[item.line_index]
  );

  // Whether "add as group" is active for a line — defaults ON the moment a group is detected,
  // same as the manual Add Item flow, until the operator explicitly flips it off.
  const groupToggleFor = (lineIndex) => groupToggles[lineIndex] ?? true;

  const handleSelectCandidate = async (lineIndex, runningSize) => {
    if (!data?.id) return;
    setPatchingLine(lineIndex);
    try {
      const updated = await patchPoItem(data.id, {
        line_index: lineIndex,
        running_size_id: runningSize.id,
      });
      setLocalIngestion(updated);
    } catch (err) {
      toast.error("Failed to update match: " + (err?.response?.data?.message || err.message));
    } finally {
      setPatchingLine(null);
    }
  };

  // For a genuinely new item that just doesn't exist in the catalogue yet — clears any
  // auto-selected running size (even a coincidental low-confidence one) so the row falls back to
  // plain extracted OCR data instead of a wrong catalogue pick.
  const handleMarkAsNew = async (lineIndex) => {
    if (!data?.id) return;
    setPatchingLine(lineIndex);
    try {
      const updated = await patchPoItem(data.id, { line_index: lineIndex, mark_as_new: true });
      setLocalIngestion(updated);
    } catch (err) {
      toast.error("Failed to update match: " + (err?.response?.data?.message || err.message));
    } finally {
      setPatchingLine(null);
    }
  };

  const handleConfirm = () => {
    if (hasBlockedRate) {
      toast.warning("Please acknowledge the rate deviations flagged in red before confirming");
      return;
    }

    if (!data) return;

    const formItems = items.flatMap((resolvedItem) => {
      const itemGroup = resolvedItem.match?.item_group;
      const addAsGroup = !!itemGroup && groupToggleFor(resolvedItem.line_index);
      const parentItem = resolvedItemToFormItem(resolvedItem, addAsGroup);
      if (!addAsGroup) return [parentItem];
      return [parentItem, ...buildGroupMemberItems(itemGroup, parentItem.quantity)];
    });
    const formHeader = {
      customer_id: customerResult.customer?.id || null,
      customer_po_number: header.po_number || "",
      customer_po_date: header.po_date || null,
      expected_delivery: header.expected_delivery || null,
      shipping_address: header.shipping_address || "",
      billing_address: header.billing_address || "",
      notes: header.notes || "",
      subtotal: header.subtotal ?? null,
      tax_amount: header.tax_amount ?? null,
      total_amount: header.total_amount ?? null,
      po_number: header.po_number || "",
    };

    onConfirm({ header: formHeader, items: formItems, ingestionId: data.id });
  };

  // ── Line items table columns ──────────────────────────────────────────

  const columns = [
    {
      title: "#",
      dataIndex: "line_index",
      width: 40,
      render: (v) => <Text type="secondary">{v}</Text>,
    },
    {
      title: "PO Description",
      dataIndex: "raw_description",
      width: 200,
      ellipsis: true,
      render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: "Match",
      key: "match",
      width: 240,
      render: (_, record) => {
        const { status, matched_running_size, candidates, item_group: itemGroup } = record.match || {};
        const isPatchingThis = patchingLine === record.line_index;
        // The best-scoring candidate is always auto-selected by the resolver now (as long as
        // it clears a low floor) — "ambiguous" just flags it for a second look, it doesn't mean
        // nothing was picked. Only true "unmatched" has no pre-selected value.
        const hasSelection = status !== "unmatched" && !!matched_running_size;
        const groupOn = groupToggleFor(record.line_index);

        return (
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <Space size={4}>
              {matchIcon(status)}
              {hasSelection ? (
                <Text style={{ fontSize: 12 }} strong>
                  {matched_running_size.name}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No match
                </Text>
              )}
              {confidenceBadge(record.match?.confidence)}
            </Space>

            <Select
              size="small"
              showSearch
              loading={isPatchingThis}
              placeholder="Select a running size"
              value={hasSelection ? matched_running_size.id : NEW_ITEM_VALUE}
              style={{ width: "100%", fontSize: 12 }}
              dropdownMatchSelectWidth={380}
              optionLabelProp="label"
              filterOption={(input, option) =>
                (option.searchText || "").toLowerCase().includes(input.toLowerCase())
              }
              onChange={(val) => {
                if (val === NEW_ITEM_VALUE) {
                  handleMarkAsNew(record.line_index);
                  return;
                }
                const rs = (candidates || []).find((c) => c.id === val);
                if (rs) handleSelectCandidate(record.line_index, rs);
              }}
            >
              <Option value={NEW_ITEM_VALUE} label="➕ New item" searchText="new item add create not in catalogue">
                <Text type="success" strong style={{ fontSize: 12 }}>
                  ➕ New item — use extracted specs (no catalogue match)
                </Text>
              </Option>
              {renderRunningSizeOptions(candidates)}
            </Select>

            {/* This running size is a defined Item Group's parent — mirrors the manual Add
                Item flow's item-group detection, defaulting to ON so the operator doesn't have
                to know to look for it. */}
            {itemGroup && (
              <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 4, padding: 4 }}>
                <Space size={4} style={{ width: "100%", justifyContent: "space-between" }}>
                  <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>
                    🔗 {itemGroup.name}
                  </Tag>
                  <Switch
                    size="small"
                    checked={groupOn}
                    onChange={(checked) =>
                      setGroupToggles((prev) => ({ ...prev, [record.line_index]: checked }))
                    }
                  />
                </Space>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {groupOn ? "Adding as group:" : "Add as group (off)"}
                </Text>
                {groupOn && (
                  <div>
                    {(itemGroup.members || []).map((m) => (
                      <Tooltip
                        key={m.running_size_id}
                        title={`${m.qty_per_group} × ${m.name}${m.is_parent ? " (parent)" : " — uses last sale price"}`}
                      >
                        <Tag style={{ fontSize: 10, marginTop: 2 }}>
                          {m.qty_per_group}× {m.name}
                          {m.is_parent ? " (parent)" : ""}
                        </Tag>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Space>
        );
      },
    },
    {
      title: "Qty",
      key: "qty",
      width: 80,
      render: (_, record) => {
        const qc = record.quantity_check;
        const flagged = qc && qc.severity === "warn";
        return (
          <Space direction="vertical" size={0}>
            <Text type={flagged ? "danger" : undefined}>
              {record.extracted?.quantity?.toLocaleString()}
            </Text>
            {flagged && (
              <Tooltip
                title={`Qty × Rate = ₹${qc.computed_amount.toLocaleString("en-IN")}, but the PO's printed amount is ₹${qc.printed_amount.toLocaleString("en-IN")}. This quantity may have been read from the wrong column — double-check it.`}
              >
                <Tag color="red" style={{ fontSize: 10, marginTop: 2 }}>
                  ⚠ verify qty
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Rate",
      key: "rate",
      width: 80,
      render: (_, record) => (
        <Text>₹{record.extracted?.rate?.toFixed(2)}</Text>
      ),
    },
    {
      title: "vs Last Price",
      key: "rate_check",
      width: 110,
      render: (_, record) => {
        const rc = record.rate_check;
        if (!rc || rc.severity === "none") return <Text type="secondary">—</Text>;
        const isBlock = rc.severity === "block";
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              {rateTag(rc)}
              <Text type="secondary" style={{ fontSize: 11 }}>
                ref ₹{rc.reference?.toFixed(2)}
              </Text>
            </Space>
            {isBlock && !rateAcknowledged[record.line_index] && (
              <Button
                size="small"
                danger
                onClick={() =>
                  setRateAcknowledged((p) => ({
                    ...p,
                    [record.line_index]: true,
                  }))
                }
              >
                Acknowledge
              </Button>
            )}
            {isBlock && rateAcknowledged[record.line_index] && (
              <Tag color="green">Acknowledged</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      width: 90,
      render: (_, record) => {
        // Prefer the amount actually printed on the PO — it's frequently the more reliable
        // number when quantity/rate extraction disagrees with it (e.g. quantity misread from
        // the wrong table column). Recomputing qty×rate here would silently amplify that error
        // instead of surfacing the mismatch.
        const extractedAmount = record.extracted?.amount;
        const a = extractedAmount != null
          ? extractedAmount
          : (record.extracted?.quantity || 0) * (record.extracted?.rate || 0);
        return <Text>₹{a.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</Text>;
      },
    },
  ];

  // ── Summary counts ────────────────────────────────────────────────────

  const stats = resolved.stats || {};
  const matchedCount = stats.items_matched || 0;
  const ambiguousCount = stats.items_ambiguous || 0;
  const total = stats.items_total || items.length;
  const overallConf = stats.overall_confidence ?? data?.overall_confidence ?? null;

  const footerButtons = [
    <Button key="discard" onClick={onDiscard} danger>
      Discard
    </Button>,
    <Button key="close" onClick={onClose}>
      Close
    </Button>,
    <Button
      key="confirm"
      type="primary"
      disabled={hasBlockedRate || total === 0}
      onClick={handleConfirm}
      style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
    >
      Use this PO data
    </Button>,
  ];

  return (
    <Modal
      open={open && !!data}
      title={
        <Space>
          <span>Review Extracted PO</span>
          {overallConf != null && (
            <Tag color={overallConf >= 0.85 ? "green" : overallConf >= 0.6 ? "orange" : "red"}>
              {Math.round(overallConf * 100)}% confidence
            </Tag>
          )}
        </Space>
      }
      width={900}
      onCancel={onClose}
      afterClose={afterClose}
      destroyOnClose
      footer={footerButtons}
    >
      {/* Customer match */}
      <Alert
        type={
          customerResult.confidence >= 0.85
            ? "success"
            : customerResult.confidence > 0
            ? "warning"
            : "error"
        }
        message={
          customerResult.customer ? (
            <Space>
              <CheckCircleOutlined />
              <Text strong>{customerResult.customer.name}</Text>
              <Text type="secondary">({customerResult.method})</Text>
              {confidenceBadge(customerResult.confidence)}
            </Space>
          ) : (
            <Space>
              <ExclamationCircleOutlined />
              <Text>Customer not matched — select manually in the form</Text>
              {customerResult.candidates?.length > 0 && (
                <Text type="secondary">
                  Possible: {customerResult.candidates.map((c) => c.name).join(", ")}
                </Text>
              )}
            </Space>
          )
        }
        style={{ marginBottom: 12 }}
      />

      {/* PO header */}
      <Descriptions bordered size="small" column={3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="PO Number">{header.po_number || "—"}</Descriptions.Item>
        <Descriptions.Item label="PO Date">{header.po_date || "—"}</Descriptions.Item>
        <Descriptions.Item label="Expected Delivery">
          {header.expected_delivery || "—"}
        </Descriptions.Item>
        {header.subtotal != null && (
          <Descriptions.Item label="Subtotal">{header.subtotal}</Descriptions.Item>
        )}
        {header.tax_amount != null && (
          <Descriptions.Item label="Tax Amount">{header.tax_amount}</Descriptions.Item>
        )}
        {header.total_amount != null && (
          <Descriptions.Item label="Total Amount">{header.total_amount}</Descriptions.Item>
        )}
        {header.shipping_address && (
          <Descriptions.Item label="Ship To" span={3}>
            {header.shipping_address}
          </Descriptions.Item>
        )}
        {header.notes && (
          <Descriptions.Item label="Notes" span={3}>
            {header.notes}
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Match summary */}
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col>
          <Badge count={matchedCount} color="green" offset={[2, 0]}>
            <Tag color="green">Matched</Tag>
          </Badge>
        </Col>
        <Col>
          <Badge count={ambiguousCount} color="orange" offset={[2, 0]}>
            <Tag color="orange">Needs Review</Tag>
          </Badge>
        </Col>
        <Col>
          <Badge count={total - matchedCount - ambiguousCount} color="red" offset={[2, 0]}>
            <Tag color="red">Unmatched</Tag>
          </Badge>
        </Col>
      </Row>

      {/* Line items */}
      <Table
        dataSource={items}
        columns={columns}
        rowKey="line_index"
        size="small"
        pagination={false}
        scroll={{ x: 700 }}
        rowClassName={(record) => {
          const s = record.match?.status;
          if (s === "matched") return "";
          if (s === "ambiguous") return "";
          return "";
        }}
      />

      <Divider style={{ margin: "12px 0" }} />
      <Text type="secondary" style={{ fontSize: 11 }}>
        Fields will pre-fill the form — you can edit any value before saving the{" "}
        {onConfirm ? "order / quotation" : "document"}.
        {hasBlockedRate &&
          " ⚠ Some rates deviate &gt;5% from last sale price — acknowledgement required."}
      </Text>
    </Modal>
  );
};

export default PoIngestReviewModal;
